import { FieldError, UseFormClearErrors, UseFormGetValues, UseFormSetError } from 'react-hook-form';

import type { AddTemplateFormSchema } from '../validations/add-template.validation';

import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useAppMutation, useDebounce, useLocale } from '../../../../hooks/index';
import ApiService from '../../../../services/api-service.util';

export type UseCheckTemplateNameParams = {
  clearErrors: UseFormClearErrors<AddTemplateFormSchema>;
  excludeTemplateId?: string;
  getFieldError: () => FieldError | undefined;
  getValues: UseFormGetValues<AddTemplateFormSchema>;
  setError: UseFormSetError<AddTemplateFormSchema>;
};

export const useCheckTemplateName = ({
  clearErrors,
  excludeTemplateId,
  getFieldError,
  getValues,
  setError,
}: UseCheckTemplateNameParams) => {
  const { messagesLocale } = useLocale();

  const { mutate } = useAppMutation([QueryKey.CHECK_TEMPLATE_NAME, excludeTemplateId], {
    mutationFn: async (name: string) => {
      const data = await ApiService.get<{ exists: boolean }>(
        {
          endpoint: BackendRoute.TEMPLATES,
          routePath: ['check-name-exists'],
        },
        {
          queryParams: {
            name,
            ...(excludeTemplateId && { excludeTemplateId }),
          },
        },
      );
      return { exists: data.exists };
    },
    onSuccess: (data, queriedName) => {
      const currentName = getValues('stepOne.name')?.trim();
      if (currentName !== queriedName) return;

      if (data.exists) {
        setError('stepOne.name', {
          message: messagesLocale['TEMPLATE_NAME_ALREADY_EXISTS'],
          type: 'validate',
          types: { unique: true },
        });
      } else {
        const err = getFieldError();
        const isUniqueError = err?.type === 'validate' && err?.types && 'unique' in err.types;
        if (isUniqueError) clearErrors('stepOne.name');
      }
    },
    defaultErrorHandling: false,
  });

  const { debounceCallback } = useDebounce<string>({
    callback: (name) => {
      if (name?.trim()) {
        mutate(name.trim());
      }
    },
    debounce: 500,
  });

  return { checkName: debounceCallback };
};
